import './index.scss';
import { bindMediaStep, renderMediaMount } from './media';
import {
    isTelField,
    mountPhoneField,
    isCoarseE164,
    splitE164,
} from './phone';

/** Split tel UI already shows the dial code; drop CMS placeholders like "+49 …". */
function nationalNumberPlaceholder(raw) {
    const text = String(raw || '').trim();
    if (text === '' || text.startsWith('+') || text.startsWith('00')) {
        return '';
    }
    return text;
}

const SELECTOR = '[data-quick-apply="1"]';
const RESUME_PLUGIN_NS = 'tx_westfleisch_quickapply';
const RESUME_ARGUMENT = 'qaResume';
const RESUME_PARAM = `${RESUME_PLUGIN_NS}[${RESUME_ARGUMENT}]`;

function isSameOriginUrl(url) {
    try {
        const u = new URL(url, window.location.href);
        return u.origin === window.location.origin;
    } catch (e) {
        return false;
    }
}

function readResumeTokenFromUrl() {
    try {
        const params = new URL(window.location.href).searchParams;
        return String(params.get(RESUME_PARAM) || '').trim();
    } catch (e) {
        return '';
    }
}

function writeResumeTokenToUrl(token) {
    try {
        const url = new URL(window.location.href);
        const value = String(token || '').trim();
        // Drop legacy top-level ?qaResume= (triggers cHash 404 with enforceValidation).
        url.searchParams.delete(RESUME_ARGUMENT);
        if (value) {
            url.searchParams.set(RESUME_PARAM, value);
        } else {
            url.searchParams.delete(RESUME_PARAM);
        }
        // Keep content-element hash (#c123) — never put query params after the hash.
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (e) {
        // ignore history failures
    }
}

function clearResumeTokenFromUrl() {
    writeResumeTokenToUrl('');
}

function isToggleField(field) {
    const type = String(field?.type || '').toLowerCase();
    return type === 'toggle' || type === 'checkbox';
}

/** `location_later` → hides `location` (same-step skip pattern). */
function toggleHidesFieldKey(toggleKey) {
    const key = String(toggleKey || '');
    if (!key.endsWith('_later')) {
        return '';
    }
    return key.slice(0, -'_later'.length);
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Comma-separated answer keys (multi-select) or a single key. */
function parseAnswerKeys(value) {
    if (Array.isArray(value)) {
        return value.map((v) => String(v).trim()).filter(Boolean);
    }
    return String(value ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function readJsonScript(root, selector) {
    const el = root.querySelector(selector);
    if (!el) {
        return null;
    }
    try {
        return JSON.parse(el.textContent || 'null');
    } catch (e) {
        return null;
    }
}

/**
 * POST Extbase plugin arguments (form-urlencoded) + Accept JSON.
 * Plugin namespace: tx_westfleisch_quickapply
 */
async function postPlugin(url, { language = '', answers = {}, captchaResponse = '' } = {}) {
    if (!isSameOriginUrl(url)) {
        throw new Error('Cross-origin request blocked');
    }
    const body = new URLSearchParams();
    body.set('tx_westfleisch_quickapply[language]', language || '');
    Object.keys(answers || {}).forEach((key) => {
        body.set(`tx_westfleisch_quickapply[answers][${key}]`, String(answers[key] ?? ''));
    });
    if (captchaResponse) {
        body.set('tx_westfleisch_quickapply[frcCaptchaResponse]', captchaResponse);
    }
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
    });
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }
    return res.json();
}

/**
 * POST plugin args as multipart (answers + optional media file). XHR for upload progress.
 * @returns {Promise<Record<string, mixed>>}
 */
function postPluginMultipart(url, {
    language = '',
    answers = {},
    captchaResponse = '',
    file = null,
    onUploadProgress = null,
    onBodySent = null,
} = {}) {
    if (!isSameOriginUrl(url)) {
        return Promise.reject(new Error('Cross-origin request blocked'));
    }
    return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('tx_westfleisch_quickapply[language]', language || '');
        Object.keys(answers || {}).forEach((key) => {
            formData.append(`tx_westfleisch_quickapply[answers][${key}]`, String(answers[key] ?? ''));
        });
        if (captchaResponse) {
            formData.append('tx_westfleisch_quickapply[frcCaptchaResponse]', captchaResponse);
        }
        if (file) {
            formData.append('tx_westfleisch_quickapply[media]', file, file.name || 'recording.webm');
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.withCredentials = true;
        xhr.upload.addEventListener('progress', (e) => {
            if (typeof onUploadProgress === 'function') {
                if (e.lengthComputable && e.total > 0) {
                    onUploadProgress(Math.round((e.loaded / e.total) * 100));
                } else {
                    onUploadProgress(0);
                }
            }
        });
        xhr.upload.addEventListener('load', () => {
            if (typeof onBodySent === 'function') {
                onBodySent();
            }
        });
        xhr.addEventListener('load', () => {
            let parsed = null;
            try {
                parsed = JSON.parse(xhr.responseText || 'null');
            } catch (err) {
                reject(new Error('invalid_json'));
                return;
            }
            if (xhr.status >= 200 && xhr.status < 300 && parsed && typeof parsed === 'object') {
                resolve(parsed);
                return;
            }
            const error = new Error(parsed?.message || 'submit_failed');
            error.payload = parsed;
            reject(error);
        });
        xhr.addEventListener('error', () => reject(new Error('network')));
        xhr.addEventListener('abort', () => reject(new Error('aborted')));
        xhr.send(formData);
    });
}

function tryAttachFriendlyCaptcha(roots, attempt = 0) {
    if (!roots || roots.length <= 0) {
        return;
    }
    try {
        const frc = window.frcaptcha;
        if (frc && typeof frc.attach === 'function') {
            frc.attach(roots);
            return;
        }
    } catch (e) {
        // SDK attach can throw if a node was already bound.
    }
    if (attempt < 10) {
        window.setTimeout(() => tryAttachFriendlyCaptcha(roots, attempt + 1), 200);
    }
}

class QuickApplyApp {
    constructor(root) {
        this.root = root;
        this.stage = root.querySelector('[data-qa-stage]');
        this.status = root.querySelector('[data-qa-status]');
        this.resolveUrl = root.getAttribute('data-resolve-url') || '';
        this.submitUrl = root.getAttribute('data-submit-url') || '';
        this.flagBaseUrl = root.getAttribute('data-flag-base') || '';
        this.captchaSiteKey = root.getAttribute('data-captcha-sitekey') || '';
        this.captchaEu = root.getAttribute('data-captcha-eu') === '1';
        this.captchaSkip = root.getAttribute('data-captcha-skip') === '1';
        this.uploadToken = '';
        this.labelsByLanguage = readJsonScript(root, '[data-qa-labels-by-language]') || {};
        this.resolved = readJsonScript(root, '[data-qa-resolved]');
        this.resumeBootstrap = readJsonScript(root, '[data-qa-resume]') || { active: false };
        this.avatarSrc = root.getAttribute('data-avatar-src') || '/favicon/wf/apple-touch-icon.png';
        this.answers = {};
        /**
         * Fields-step keys that the applicant has advanced past.
         * Kept out of `answers` so a step key matching a field key (e.g. both
         * `location`) cannot overwrite the entered value with a sentinel `1`.
         * @type {Set<string>}
         */
        this.committedFieldsSteps = new Set();
        this.language = '';
        this.stepIndex = 0;
        this.busy = false;
        this.done = false;
        this.submitted = false;
        /** True when the media step was skipped (summary recap). */
        this.mediaSkipped = false;
        /** @type {{ file: File, kind?: string|null, durationSeconds?: number|null }|null} */
        this.pendingMedia = null;
        this.labels = {};
        /** @type {null|'bottom'} Scroll after the next render; null skips (initial load). */
        this.pendingScroll = null;
        this.submitOverlay = this.root.querySelector('[data-qa-submit-overlay]');
        this.submitOverlayHome = null;

        if (!this.stage || !this.resolved || !Array.isArray(this.resolved.steps)) {
            this.applyLabels();
            this.showStatus(this.labels.errorNoFunnel || 'Error', true);
            return;
        }

        this.language = String(this.resolved.language || '');
        this.applyLabels();
        this.applyResumeBootstrap();
        this.render();
    }

    /**
     * After BITE submit, plugin arg qaResume + server bootstrap land on thanks.
     */
    applyResumeBootstrap() {
        const resume = this.resumeBootstrap && typeof this.resumeBootstrap === 'object'
            ? this.resumeBootstrap
            : { active: false };
        const urlToken = readResumeTokenFromUrl();

        if (!resume.active) {
            if (urlToken || resume.error) {
                clearResumeTokenFromUrl();
            }
            return;
        }

        const token = String(resume.token || urlToken || '').trim();
        if (!token) {
            clearResumeTokenFromUrl();
            return;
        }

        this.submitted = true;
        this.uploadToken = token;
        writeResumeTokenToUrl(token);

        const resumeLanguage = String(resume.language || '').toLowerCase();
        if (resumeLanguage) {
            this.language = resumeLanguage;
            this.applyLabels();
        }

        const steps = this.visibleSteps();
        const messageIdx = steps.findIndex((s) => s.layout === 'message');
        if (messageIdx >= 0) {
            this.stepIndex = messageIdx;
        }
    }

    applyLabels() {
        const lang = String(this.language || '').toLowerCase();
        const map = this.labelsByLanguage || {};
        this.labels = map[lang] || map.en || map.de || Object.values(map)[0] || {};
    }

    /**
     * Visibility for steps/options/fields: `always`, or `any` of option UIDs.
     * FE owns this after language pick; PHP exposes the same rules via
     * QuickApplyResolveService::filterVisibleByAnswers() (BE preview).
     */
    isVisible(visibility, selectedOptionUids) {
        const mode = String(visibility?.mode || 'always');
        const optionUids = Array.isArray(visibility?.optionUids) ? visibility.optionUids : [];
        if (mode !== 'any' || optionUids.length === 0) {
            return true;
        }
        const selected = selectedOptionUids || [];
        return optionUids.some((uid) => selected.includes(Number(uid)));
    }

    /**
     * Option UIDs selected via choice-step answers (comma-separated keys).
     * @returns {number[]}
     */
    selectedOptionUids() {
        const uids = [];
        const steps = this.resolved?.steps || [];
        steps.forEach((step) => {
            if (!step || step.layout !== 'choice') {
                return;
            }
            const keys = parseAnswerKeys(this.answers[step.key]);
            if (keys.length === 0) {
                return;
            }
            (step.options || []).forEach((opt) => {
                if (keys.includes(String(opt.key))) {
                    uids.push(Number(opt.uid));
                }
            });
        });
        return uids;
    }

    /**
     * Full graph filtered to currently visible steps (options/fields filtered too).
     * Navigation / render use this; `this.resolved.steps` stays the full graph.
     */
    visibleSteps() {
        const selected = this.selectedOptionUids();
        const steps = (this.resolved?.steps || [])
            .filter((step) => this.isVisible(step.visibility, selected))
            .map((step) => ({
                ...step,
                options: (step.options || []).filter((opt) => this.isVisible(opt.visibility, selected)),
                fields: (step.fields || []).filter((field) => this.isVisible(field.visibility, selected)),
            }));
        return this.orderMediaBeforeSummary(steps);
    }

    /**
     * Older funnels had media after summary. Keep video as the step before review.
     * @param {Array<Record<string, mixed>>} steps
     */
    orderMediaBeforeSummary(steps) {
        const mediaIdx = steps.findIndex((s) => s.layout === 'media');
        const summaryIdx = steps.findIndex((s) => s.layout === 'summary');
        if (mediaIdx < 0 || summaryIdx < 0 || mediaIdx < summaryIdx) {
            return steps;
        }
        const next = [...steps];
        const [media] = next.splice(mediaIdx, 1);
        const insertAt = next.findIndex((s) => s.layout === 'summary');
        next.splice(insertAt, 0, media);
        return next;
    }

    currentStep() {
        const steps = this.visibleSteps();
        return steps[this.stepIndex] || null;
    }

    /** Index of the first summary step (submit gate), or -1. */
    summaryIndex() {
        return this.visibleSteps().findIndex((s) => s.layout === 'summary');
    }

    maxPreSubmitIndex() {
        const messageIdx = this.visibleSteps().findIndex((s) => s.layout === 'message');
        if (messageIdx > 0) {
            return messageIdx - 1;
        }
        const summaryIdx = this.summaryIndex();
        if (summaryIdx >= 0) {
            return summaryIdx;
        }
        return Math.max(0, this.visibleSteps().length - 1);
    }

    firstPostSubmitIndex() {
        return this.visibleSteps().findIndex((s) => s.layout === 'message');
    }

    clampStepIndex(idx) {
        const steps = this.visibleSteps();
        if (steps.length === 0) {
            return 0;
        }
        let next = Math.max(0, Math.min(idx, steps.length - 1));
        if (!this.submitted) {
            next = Math.min(next, this.maxPreSubmitIndex());
        } else {
            const firstPost = this.firstPostSubmitIndex();
            if (firstPost >= 0) {
                next = Math.max(next, firstPost);
            }
        }
        return next;
    }

    /**
     * Advance / stay within the visible step list without a network round-trip.
     */
    advanceLocal({ advance = true, stayOnKey = null } = {}) {
        const prevKey = stayOnKey || this.currentStep()?.key || null;
        this.stepIndex = this.clampStepIndex(this.findStepIndex(prevKey, advance));
        this.pendingScroll = 'bottom';
        this.render();
    }

    setBusy(busy) {
        this.busy = busy;
        this.root.classList.toggle('is-busy', busy);
        if (busy) {
            this.showStatus(this.labels.loading || '…', false);
        } else if (!this.done) {
            this.hideStatus();
        }
    }

    showStatus(message, isError) {
        if (!this.status) {
            return;
        }
        this.status.hidden = false;
        this.status.textContent = message;
        this.status.classList.toggle('quick-apply__status--error', !!isError);
    }

    hideStatus() {
        if (!this.status) {
            return;
        }
        this.status.hidden = true;
        this.status.textContent = '';
        this.status.classList.remove('quick-apply__status--error');
    }

    async refreshResolved({ advance = false, stayOnKey = null } = {}) {
        this.setBusy(true);
        try {
            const next = await postPlugin(this.resolveUrl, {
                language: this.language,
                answers: this.answers,
            });
            if (!next || next.error || !Array.isArray(next.steps)) {
                throw new Error(next?.error || 'resolve_failed');
            }
            const prevKey = stayOnKey || this.currentStep()?.key || null;
            this.resolved = next;
            if (this.language === '' && next.language) {
                this.language = String(next.language);
            }
            this.applyLabels();
            this.stepIndex = this.clampStepIndex(this.findStepIndex(prevKey, advance));
            this.pendingScroll = 'bottom';
            this.render();
        } catch (e) {
            this.showStatus(this.labels.errorGeneric || 'Error', true);
        } finally {
            this.setBusy(false);
        }
    }

    findStepIndex(prevKey, advance) {
        const steps = this.visibleSteps();
        if (steps.length === 0) {
            return 0;
        }
        if (!prevKey) {
            return 0;
        }
        const idx = steps.findIndex((s) => s.key === prevKey);
        if (idx < 0) {
            return Math.min(this.stepIndex, steps.length - 1);
        }
        if (advance) {
            return Math.min(idx + 1, steps.length - 1);
        }
        return idx;
    }

    /**
     * Clear answers owned by a step (choice key, or fields + commit marker).
     */
    clearStepAnswers(step) {
        if (!step) {
            return;
        }
        if (step.layout === 'fields') {
            this.committedFieldsSteps.delete(step.key);
            // Drop legacy sentinel if present (older builds wrote answers[step.key] = '1').
            delete this.answers[step.key];
            (step.fields || []).forEach((field) => {
                delete this.answers[field.key];
            });
            return;
        }
        if (step.layout === 'language' || step.layout === 'choice') {
            delete this.answers[step.key];
            return;
        }
        if (step.layout === 'privacy') {
            delete this.answers.privacy;
        }
    }

    /**
     * Unwind answers from a visible-step index onward (inclusive).
     * Clears full-graph steps from that step's funnel position so hidden-branch
     * answers cannot keep unlocking conditional steps.
     */
    clearAnswersFrom(fromVisibleIndex) {
        const visible = this.visibleSteps();
        const target = visible[fromVisibleIndex];
        const all = this.resolved?.steps || [];
        let start = fromVisibleIndex;
        if (target) {
            const fullIdx = all.findIndex((s) => s.key === target.key);
            if (fullIdx >= 0) {
                start = fullIdx;
            }
        }
        for (let i = start; i < all.length; i += 1) {
            this.clearStepAnswers(all[i]);
        }
    }

    answerLabels(stepKey, value) {
        const steps = this.resolved.steps || [];
        const keys = parseAnswerKeys(value);
        for (const step of steps) {
            if (step.key === stepKey && Array.isArray(step.options)) {
                const labels = [];
                keys.forEach((key) => {
                    const opt = step.options.find((o) => o.key === key);
                    if (opt) {
                        labels.push(opt.label);
                    }
                });
                if (labels.length) {
                    return labels;
                }
            }
            if (Array.isArray(step.fields)) {
                const field = step.fields.find((f) => f.key === stepKey);
                if (field) {
                    return [field.label];
                }
            }
        }
        return keys.length ? keys : [stepKey];
    }

    answerLabel(stepKey, value) {
        return this.answerLabels(stepKey, value).join(', ');
    }

    /**
     * User-bubble texts for a committed step (multi-select → one bubble per option).
     * @returns {string[]}
     */
    formatCommittedAnswerBubbles(step) {
        if (!step) {
            return [];
        }
        if (step.layout === 'language' || step.layout === 'choice') {
            const val = this.answers[step.key];
            if (val == null || val === '') {
                return [];
            }
            if (step.allowMultiple) {
                return this.answerLabels(step.key, val);
            }
            const label = this.answerLabel(step.key, val);
            return label ? [label] : [];
        }
        if (step.layout === 'privacy') {
            const val = this.answers.privacy;
            if (val === '1' || val === 'true') {
                return [this.labels.privacyAcceptedSummary || 'Accepted'];
            }
            return [];
        }
        if (step.layout === 'fields') {
            const lines = [];
            (step.fields || []).forEach((field) => {
                const val = this.answers[field.key];
                if (isToggleField(field)) {
                    if (val !== '1' && val !== 'true') {
                        return;
                    }
                    const hides = toggleHidesFieldKey(field.key);
                    if (hides) {
                        const hiddenField = (step.fields || []).find((f) => f.key === hides);
                        const label = hiddenField?.label || field.label;
                        lines.push(`${label}: ${field.label}`);
                        return;
                    }
                    lines.push(field.label);
                    return;
                }
                const hideKey = `${field.key}_later`;
                if (this.answers[hideKey] === '1' || this.answers[hideKey] === 'true') {
                    return;
                }
                if (val == null || val === '') {
                    return;
                }
                lines.push(`${field.label}: ${val}`);
            });
            return lines.length ? [lines.join('\n')] : [];
        }
        return [];
    }

    isStepCommitted(step) {
        if (!step) {
            return false;
        }
        if (step.layout === 'language' || step.layout === 'choice') {
            const val = this.answers[step.key];
            return val != null && val !== '';
        }
        if (step.layout === 'privacy') {
            const val = this.answers.privacy;
            return val === '1' || val === 'true';
        }
        if (step.layout === 'fields') {
            return this.committedFieldsSteps.has(step.key);
        }
        return false;
    }

    render() {
        if (this.done) {
            return;
        }
        const steps = this.visibleSteps();
        if (steps.length === 0) {
            this.stage.innerHTML = `<p class="quick-apply__error">${escapeHtml(this.labels.errorGeneric)}</p>`;
            return;
        }
        this.stepIndex = this.clampStepIndex(this.stepIndex);
        const step = this.currentStep();
        if (!step) {
            return;
        }
        // Thanks is post-submit only
        if (!this.submitted && step.layout === 'message' && this.maxPreSubmitIndex() >= 0) {
            this.stepIndex = this.maxPreSubmitIndex();
            return this.render();
        }

        if (this.submitted && step.layout === 'message') {
            this.renderPostSubmit(step);
            this.runPendingScroll();
            return;
        }
        if (this.submitted) {
            const messageIdx = this.firstPostSubmitIndex();
            if (messageIdx >= 0) {
                this.stepIndex = messageIdx;
                return this.render();
            }
        }

        this.stage.innerHTML = this.renderChat(step);
        this.bindCurrentStep(step);
        this.runPendingScroll();
    }

    renderPostSubmit(step) {
        if (step.layout === 'message') {
            this.stage.innerHTML = this.renderMessage(step);
            this.bindMessage();
            return;
        }
        this.stage.innerHTML = `<p class="quick-apply__error">${escapeHtml(this.labels.errorGeneric)}</p>`;
    }

    /** Content-element shell (includes frame padding when present). */
    pluginShell() {
        return this.root.closest('.frame') || this.root.closest('.quick-apply') || this.root;
    }

    runPendingScroll() {
        const mode = this.pendingScroll;
        this.pendingScroll = null;
        if (mode === 'bottom') {
            this.scrollPluginBottomIntoView();
        }
    }

    /**
     * Align the plugin frame bottom with the viewport bottom.
     */
    scrollPluginBottomIntoView() {
        const el = this.pluginShell();
        if (!el) {
            return;
        }
        const run = () => {
            const rect = el.getBoundingClientRect();
            const delta = rect.bottom - window.innerHeight;
            if (Math.abs(delta) > 1) {
                window.scrollBy({ top: delta, left: 0, behavior: 'smooth' });
            }
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
    }

    renderAgentHeading(step, { includeHelp = true } = {}) {
        const help = includeHelp && step.help
            ? `<p class="quick-apply__help">${escapeHtml(step.help)}</p>`
            : '';
        return `
			<div class="quick-apply__bubble-head">
				<strong class="quick-apply__title">${escapeHtml(step.title || this.resolved.title || '')}</strong>
				${help}
			</div>
		`;
    }

    renderAgentRow(innerHtml) {
        const avatar = this.avatarSrc
            ? `<img class="quick-apply__avatar" src="${escapeHtml(this.avatarSrc)}" alt="" width="48" height="48" decoding="async" />`
            : `<div class="quick-apply__avatar" aria-hidden="true"></div>`;
        return `
			<div class="quick-apply__row">
				${avatar}
				<div class="quick-apply__bubble">${innerHtml}</div>
			</div>
		`;
    }

    renderUserRow(texts) {
        const list = (Array.isArray(texts) ? texts : [texts])
            .map((t) => (t == null ? '' : String(t)))
            .filter(Boolean);
        if (!list.length) {
            return '';
        }
        const bubbles = list.map((text) => {
            const html = escapeHtml(text).replace(/\n/g, '<br>');
            return `<div class="quick-apply__bubble">${html}</div>`;
        }).join('');
        return `
			<div class="quick-apply__row quick-apply__row--user">
				${bubbles}
			</div>
		`;
    }

    progressHtml() {
        const steps = this.visibleSteps();
        if (steps.length <= 1) {
            return '';
        }
        return `<span class="quick-apply__progress" data-qa-progress>${escapeHtml(`${this.stepIndex + 1} / ${steps.length}`)}</span>`;
    }

    renderNav({
        showBack = true,
        showNext = false,
        showSkip = false,
        showSubmit = false,
        nextDisabled = false,
        showProgress = true,
        backLabel = '',
    } = {}) {
        const progress = showProgress ? this.progressHtml() : '';
        const backText = backLabel || this.labels.back;
        const back = showBack && this.stepIndex > 0
            ? `<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-back>${escapeHtml(backText)}</button>`
            : '';
        const skip = showSkip
            ? `<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-skip>${escapeHtml(this.labels.skip)}</button>`
            : '';
        const nextDisabledAttr = nextDisabled ? ' disabled aria-disabled="true"' : '';
        const next = showNext
            ? `<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-next${nextDisabledAttr}>${escapeHtml(this.labels.next)}</button>`
            : '';
        const submit = showSubmit
            ? `<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-submit>${escapeHtml(this.labels.submit)}</button>`
            : '';
        return `
			<nav class="quick-apply__nav" aria-label="Quick Apply">
				<div class="quick-apply__nav-start">${back}${skip}</div>
				<div class="quick-apply__nav-center">${progress}</div>
				<div class="quick-apply__nav-end">${next}${submit}</div>
			</nav>
		`;
    }

    /**
     * Full chat: committed history + active step (options/fields only on active agent bubble).
     */
    renderChat(activeStep) {
        const steps = this.visibleSteps();
        let history = '';

        for (let i = 0; i < this.stepIndex; i += 1) {
            const step = steps[i];
            if (!step || step.layout === 'summary' || step.layout === 'media' || step.layout === 'message') {
                continue;
            }
            if (!this.isStepCommitted(step)) {
                continue;
            }
            // Committed: question only (no options/fields)
            history += this.renderAgentRow(this.renderAgentHeading(step, { includeHelp: false }));
            history += this.renderUserRow(this.formatCommittedAnswerBubbles(step));
        }

        let active = '';
        if (activeStep.layout === 'language' || activeStep.layout === 'choice') {
            active = this.renderAgentRow(
                this.renderAgentHeading(activeStep) + this.renderOptions(activeStep)
            );
        } else if (activeStep.layout === 'fields') {
            active = this.renderAgentRow(
                this.renderAgentHeading(activeStep) + this.renderFieldsInner(activeStep)
            );
        } else if (activeStep.layout === 'privacy') {
            const privacyStep = {
                ...activeStep,
                help: activeStep.intro || activeStep.help || '',
            };
            active = this.renderAgentRow(
                this.renderAgentHeading(privacyStep) + this.renderPrivacyInner(activeStep)
            );
        } else if (activeStep.layout === 'summary') {
            active = this.renderAgentRow(
                this.renderAgentHeading(activeStep)
                + this.renderSummaryRecap()
                + this.renderCaptcha()
            );
        } else if (activeStep.layout === 'media') {
            active = this.renderAgentRow(
                this.renderAgentHeading(activeStep) + renderMediaMount({ labels: this.labels })
            );
        } else {
            active = this.renderAgentRow(this.renderAgentHeading(activeStep));
        }

        const showBack = this.stepIndex > 0;
        const showSkip = activeStep.layout === 'media';
        const showNext = activeStep.layout === 'fields'
            || activeStep.layout === 'privacy'
            || activeStep.layout === 'media'
            || (activeStep.layout === 'choice' && !!activeStep.allowMultiple);
        const showSubmit = activeStep.layout === 'summary';
        let nextDisabled = false;
        if (activeStep.layout === 'choice' && activeStep.allowMultiple) {
            nextDisabled = parseAnswerKeys(this.answers[activeStep.key]).length === 0;
        } else if (activeStep.layout === 'fields') {
            nextDisabled = !this.areRequiredFieldsSatisfied(activeStep);
        } else if (activeStep.layout === 'privacy') {
            nextDisabled = !(this.answers.privacy === '1' || this.answers.privacy === 'true');
        } else if (activeStep.layout === 'media') {
            nextDisabled = !this.pendingMedia?.file;
        }

        return `
			<div class="quick-apply__chat" data-qa-chat>
				${history}
				${active}
			</div>
			${this.renderNav({ showBack, showNext, showSkip, showSubmit, nextDisabled })}
		`;
    }

    renderOptions(step) {
        const selected = new Set(parseAnswerKeys(this.answers[step.key]));
        const options = (step.options || []).map((opt) => {
            const isSelected = selected.has(opt.key) ? ' is-selected' : '';
            return `
				<button type="button" class="quick-apply__option${isSelected}" data-qa-option="${escapeHtml(opt.key)}" aria-pressed="${selected.has(opt.key) ? 'true' : 'false'}">
					<span class="quick-apply__option-label">${escapeHtml(opt.label)}</span>
					${opt.help ? `<span class="quick-apply__option-help">${escapeHtml(opt.help)}</span>` : ''}
				</button>
			`;
        }).join('');
        return `<div class="quick-apply__options" role="list">${options}</div>`;
    }

    /**
     * Compact answer recap inside the review agent bubble.
     */
    renderSummaryRecap() {
        const rows = [];
        const steps = this.visibleSteps();
        steps.forEach((s) => {
            if (s.layout === 'summary' || s.layout === 'message' || s.layout === 'media') {
                return;
            }
            if (s.layout === 'privacy') {
                if (this.answers.privacy === '1' || this.answers.privacy === 'true') {
                    rows.push(`
						<li class="quick-apply__summary-row">
							<span class="quick-apply__summary-label">${escapeHtml(s.title || s.key)}</span>
							<span class="quick-apply__summary-value">${escapeHtml(this.labels.privacyAcceptedSummary || 'Accepted')}</span>
						</li>
					`);
                }
                return;
            }
            if (s.layout === 'fields') {
                (s.fields || []).forEach((field) => {
                    const val = this.answers[field.key];
                    if (isToggleField(field)) {
                        if (val !== '1' && val !== 'true') {
                            return;
                        }
                        const hides = toggleHidesFieldKey(field.key);
                        if (hides) {
                            const hiddenField = (s.fields || []).find((f) => f.key === hides);
                            const label = hiddenField?.label || field.label;
                            rows.push(`
							<li class="quick-apply__summary-row">
								<span class="quick-apply__summary-label">${escapeHtml(label)}</span>
								<span class="quick-apply__summary-value">${escapeHtml(field.label)}</span>
							</li>
						`);
                            return;
                        }
                        rows.push(`
							<li class="quick-apply__summary-row">
								<span class="quick-apply__summary-label">${escapeHtml(field.label)}</span>
								<span class="quick-apply__summary-value">✓</span>
							</li>
						`);
                        return;
                    }
                    const hideKey = `${field.key}_later`;
                    if (this.answers[hideKey] === '1' || this.answers[hideKey] === 'true') {
                        return;
                    }
                    if (val == null || val === '') {
                        return;
                    }
                    rows.push(`
						<li class="quick-apply__summary-row">
							<span class="quick-apply__summary-label">${escapeHtml(field.label)}</span>
							<span class="quick-apply__summary-value">${escapeHtml(val)}</span>
						</li>
					`);
                });
                return;
            }
            const val = this.answers[s.key];
            if (val == null || val === '') {
                return;
            }
            const labels = this.answerLabels(s.key, val);
            let valueHtml;
            if (s.allowMultiple && labels.length > 1) {
                valueHtml = `<ul class="quick-apply__summary-list">${labels
                    .map((label) => `<li>${escapeHtml(label)}</li>`)
                    .join('')}</ul>`;
            } else {
                valueHtml = escapeHtml(labels.join(', '));
            }
            rows.push(`
				<li class="quick-apply__summary-row">
					<span class="quick-apply__summary-label">${escapeHtml(s.title || s.key)}</span>
					<span class="quick-apply__summary-value">${valueHtml}</span>
				</li>
			`);
        });
        const mediaStep = steps.find((s) => s.layout === 'media');
        if (mediaStep) {
            const mediaValue = this.pendingMedia?.file
                ? this.formatMediaSummaryValue()
                : (this.labels.summaryMediaSkipped || 'Skipped');
            rows.push(`
				<li class="quick-apply__summary-row">
					<span class="quick-apply__summary-label">${escapeHtml(mediaStep.title || mediaStep.key)}</span>
					<span class="quick-apply__summary-value">${escapeHtml(mediaValue)}</span>
				</li>
			`);
        }
        if (rows.length === 0) {
            return '';
        }
        return `<ul class="quick-apply__summary" data-qa-summary>${rows.join('')}</ul>`;
    }

    renderCaptcha() {
        const siteKey = this.captchaSiteKey;
        if (!siteKey) {
            return '';
        }
        const funnelLanguage = typeof this.language === 'string' && this.language
            ? this.language
            : 'en';
        const euAttr = this.captchaEu ? ' data-api-endpoint="eu"' : '';
        return `
			<form class="quick-apply__captcha" data-qa-captcha action="#" method="post">
				<div class="frc-captcha" data-sitekey="${escapeHtml(siteKey)}" data-lang="${escapeHtml(funnelLanguage)}" data-start="auto"${euAttr}></div>
			</form>
		`;
    }

    formatMediaSummaryValue() {
        const label = this.labels.summaryMediaRecorded || 'Video recorded';
        const dur = this.pendingMedia?.durationSeconds;
        if (dur == null || Number.isNaN(Number(dur))) {
            return label;
        }
        const total = Math.max(0, Math.round(Number(dur)));
        const m = Math.floor(total / 60);
        const s = total % 60;
        return `${label} (${m}:${String(s).padStart(2, '0')})`;
    }

    renderFieldsInner(step) {
        const fields = (step.fields || []).map((field) => {
            if (isToggleField(field)) {
                const checked = this.answers[field.key] === '1' || this.answers[field.key] === 'true';
                const hides = toggleHidesFieldKey(field.key);
                return `
					<div class="quick-apply__field quick-apply__field--toggle form-group">
						<label class="quick-apply__check" for="qa-field-${escapeHtml(field.key)}">
							<input
								id="qa-field-${escapeHtml(field.key)}"
								name="${escapeHtml(field.key)}"
								type="checkbox"
								value="1"
								data-qa-field="${escapeHtml(field.key)}"
								data-qa-toggle="1"
								data-qa-hides="${escapeHtml(hides)}"
								data-required="0"
								${checked ? 'checked' : ''}
							/>
							<span class="quick-apply__check-label">${escapeHtml(field.label)}</span>
						</label>
					</div>
				`;
            }
            const value = this.answers[field.key] != null ? String(this.answers[field.key]) : '';
            const required = field.required ? ' required' : '';
            const reqMark = field.required
                ? `<span class="quick-apply__required" title="${escapeHtml(this.labels.required)}">*</span>`
                : '';
            const type = field.type === 'email' || field.type === 'tel' || field.type === 'phone'
                ? (field.type === 'phone' ? 'tel' : field.type)
                : (field.type === 'date' ? 'date' : 'text');
            const hiddenByToggle = (step.fields || []).some(
                (f) => isToggleField(f)
                    && toggleHidesFieldKey(f.key) === field.key
                    && (this.answers[f.key] === '1' || this.answers[f.key] === 'true')
            );
            if (isTelField(field)) {
                const e164 = value.startsWith('+') ? value : '';
                const nationalPrefill = e164 ? splitE164(e164).national : value;
                return `
				<div class="quick-apply__field form-group" data-qa-field-wrap="${escapeHtml(field.key)}" ${hiddenByToggle ? 'hidden' : ''}>
					<label class="form-label" for="qa-field-${escapeHtml(field.key)}-national">
						${escapeHtml(field.label)}${reqMark}
					</label>
					<div class="quick-apply__phone" data-qa-phone>
						<button type="button" class="quick-apply__phone-trigger" data-qa-phone-trigger>
							<img class="quick-apply__phone-flag" data-qa-phone-flag alt="" width="20" height="14" />
							<span class="quick-apply__phone-dial" data-qa-phone-dial></span>
						</button>
						<input
							class="form-control quick-apply__phone-national"
							id="qa-field-${escapeHtml(field.key)}-national"
							type="tel"
							inputmode="tel"
							autocomplete="tel-national"
							placeholder="${escapeHtml(nationalNumberPlaceholder(field.placeholder))}"
							value="${escapeHtml(nationalPrefill)}"
							data-qa-phone-national
							${hiddenByToggle ? 'disabled' : ''}
						/>
						<input
							type="hidden"
							id="qa-field-${escapeHtml(field.key)}"
							name="${escapeHtml(field.key)}"
							value="${escapeHtml(e164 || value)}"
							data-qa-field="${escapeHtml(field.key)}"
							data-qa-phone-e164="1"
							data-required="${field.required ? '1' : '0'}"
							${hiddenByToggle ? 'disabled' : ''}
						/>
					</div>
				</div>
			`;
            }
            return `
				<div class="quick-apply__field form-group" data-qa-field-wrap="${escapeHtml(field.key)}" ${hiddenByToggle ? 'hidden' : ''}>
					<label class="form-label" for="qa-field-${escapeHtml(field.key)}">
						${escapeHtml(field.label)}${reqMark}
					</label>
					<div class="input">
						<input
							class="form-control"
							id="qa-field-${escapeHtml(field.key)}"
							name="${escapeHtml(field.key)}"
							type="${escapeHtml(type)}"
							value="${escapeHtml(value)}"
							placeholder="${escapeHtml(field.placeholder || '')}"
							data-qa-field="${escapeHtml(field.key)}"
							data-required="${field.required ? '1' : '0'}"
							${required}
							${hiddenByToggle ? 'disabled' : ''}
						/>
					</div>
				</div>
			`;
        }).join('');
        return `<form class="quick-apply__fields" data-qa-fields novalidate>${fields}</form>`;
    }

    renderPrivacyInner(step) {
        const accepted = this.answers.privacy === '1' || this.answers.privacy === 'true';
        const pdfUrl = step.pdfUrl || '';
        // Intro is shown via heading help (see privacy renderChat branch).
        return `
			<div class="quick-apply__privacy" data-qa-privacy>
				<button type="button" class="quick-apply__option" data-qa-privacy-open>
					<span class="quick-apply__option-label">${escapeHtml(this.labels.privacyOpen || 'Open privacy notice')}</span>
				</button>
				<label class="quick-apply__check quick-apply__privacy-check" for="qa-privacy-accept">
					<input
						id="qa-privacy-accept"
						type="checkbox"
						value="1"
						data-qa-privacy-checkbox
						${accepted ? 'checked' : ''}
					/>
					<span class="quick-apply__check-label">${escapeHtml(this.labels.privacyCheckbox || 'I have read the privacy notice')}</span>
				</label>
				<dialog class="quick-apply__privacy-dialog" data-qa-privacy-dialog>
					<div class="quick-apply__privacy-dialog-inner">
						<div class="quick-apply__privacy-body" data-qa-privacy-body></div>
						<div class="quick-apply__privacy-actions">
							${pdfUrl ? `<a class="quick-apply__nav-btn quick-apply__nav-btn--back" href="${escapeHtml(pdfUrl)}" target="_blank" rel="noopener" data-qa-privacy-pdf>${escapeHtml(this.labels.privacyDownloadPdf || 'Download PDF')}</a>` : ''}
							<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--back" data-qa-privacy-decline>${escapeHtml(this.labels.privacyDecline || 'Decline')}</button>
							<button type="button" class="quick-apply__nav-btn quick-apply__nav-btn--next" data-qa-privacy-accept>${escapeHtml(this.labels.privacyAccept || 'Accept')}</button>
						</div>
					</div>
				</dialog>
			</div>
		`;
    }

    bindCurrentStep(step) {
        this.bindBack();
        if (step.layout === 'language' || step.layout === 'choice') {
            this.bindChoice(step);
            return;
        }
        if (step.layout === 'fields') {
            this.bindFields(step);
            return;
        }
        if (step.layout === 'privacy') {
            this.bindPrivacy(step);
            return;
        }
        if (step.layout === 'summary') {
            this.bindSummary();
            return;
        }
        if (step.layout === 'media') {
            bindMediaStep(this.stage, {
                onAdvance: ({ skipped, file, kind, durationSeconds } = {}) => {
                    if (skipped) {
                        this.pendingMedia = null;
                        this.mediaSkipped = true;
                    } else if (file) {
                        this.pendingMedia = { file, kind: kind || null, durationSeconds: durationSeconds ?? null };
                        this.mediaSkipped = false;
                    }
                    this.advanceLocal({ advance: true, stayOnKey: step.key });
                },
                maxRecordSeconds: Number(this.resolved?.maxRecordSeconds) || 120,
                maxUploadBytes: Number(this.resolved?.maxUploadBytes) || 262_144_000,
                labels: this.labels,
                initialFile: this.pendingMedia?.file || null,
                initialKind: this.pendingMedia?.kind || null,
                initialDurationSeconds: this.pendingMedia?.durationSeconds ?? null,
            });
        }
    }

    bindPrivacy(step) {
        const next = this.stage.querySelector('[data-qa-next]');
        const checkbox = this.stage.querySelector('[data-qa-privacy-checkbox]');
        const dialog = this.stage.querySelector('[data-qa-privacy-dialog]');
        const openBtn = this.stage.querySelector('[data-qa-privacy-open]');
        const acceptBtn = this.stage.querySelector('[data-qa-privacy-accept]');
        const declineBtn = this.stage.querySelector('[data-qa-privacy-decline]');
        const bodyEl = this.stage.querySelector('[data-qa-privacy-body]');

        const sync = (accepted) => {
            if (accepted) {
                this.answers.privacy = '1';
                if (checkbox) {
                    checkbox.checked = true;
                }
            } else {
                delete this.answers.privacy;
                if (checkbox) {
                    checkbox.checked = false;
                }
            }
            this.setNextEnabled(next, !!accepted);
        };

        if (checkbox) {
            checkbox.addEventListener('change', () => {
                sync(!!checkbox.checked);
            });
        }
        this.setNextEnabled(next, this.answers.privacy === '1' || this.answers.privacy === 'true');

        if (openBtn && dialog && bodyEl) {
            openBtn.addEventListener('click', () => {
                bodyEl.innerHTML = step.noticeHtml || '';
                if (typeof dialog.showModal === 'function') {
                    dialog.showModal();
                } else {
                    dialog.setAttribute('open', 'open');
                }
            });
        }
        if (acceptBtn) {
            acceptBtn.addEventListener('click', () => {
                sync(true);
                if (dialog?.open) {
                    dialog.close();
                } else if (dialog) {
                    dialog.removeAttribute('open');
                }
                // Accept = consent + continue (no extra Next click).
                this.hideStatus();
                this.advanceLocal({ advance: true, stayOnKey: step.key });
            });
        }
        if (declineBtn) {
            declineBtn.addEventListener('click', () => {
                sync(false);
                if (dialog?.open) {
                    dialog.close();
                } else if (dialog) {
                    dialog.removeAttribute('open');
                }
            });
        }
        if (next) {
            next.addEventListener('click', (e) => {
                e.preventDefault();
                if (this.busy || next.disabled) {
                    return;
                }
                if (!(this.answers.privacy === '1' || this.answers.privacy === 'true')) {
                    this.showStatus(this.labels.privacyRequired || this.labels.errorGeneric, true);
                    return;
                }
                this.hideStatus();
                this.advanceLocal({ advance: true, stayOnKey: step.key });
            });
        }
    }

    setNextEnabled(next, enabled) {
        if (!next) {
            return;
        }
        next.disabled = !enabled;
        next.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }

    /**
     * Required visible fields must have a non-empty value (toggles are optional).
     */
    areRequiredFieldsSatisfied(step, form = null) {
        const fields = step?.fields || [];
        for (const field of fields) {
            if (isToggleField(field) || !field.required) {
                continue;
            }
            if (form) {
                const input = form.querySelector(`[data-qa-field="${field.key}"]`);
                if (!input || input.disabled || input.closest('[hidden]')) {
                    continue;
                }
                const val = String(input.value || '').trim();
                if (input.getAttribute('data-qa-phone-e164') === '1') {
                    if (val === '') {
                        return false;
                    }
                    if (!isCoarseE164(val)) {
                        return false;
                    }
                    continue;
                }
                if (val === '') {
                    return false;
                }
                continue;
            }
            const hiddenByToggle = fields.some(
                (f) => isToggleField(f)
                    && toggleHidesFieldKey(f.key) === field.key
                    && (this.answers[f.key] === '1' || this.answers[f.key] === 'true')
            );
            if (hiddenByToggle) {
                continue;
            }
            const val = this.answers[field.key];
            if (val == null || String(val).trim() === '') {
                return false;
            }
            if (isTelField(field) && !isCoarseE164(String(val))) {
                return false;
            }
        }
        return true;
    }

    bindChoice(step) {
        const multi = !!step.allowMultiple;
        this.stage.querySelectorAll('[data-qa-option]').forEach((btn) => {
            btn.addEventListener('click', async () => {
                if (this.busy) {
                    return;
                }
                const key = btn.getAttribute('data-qa-option') || '';
                if (!key) {
                    return;
                }

                if (multi) {
                    const keys = parseAnswerKeys(this.answers[step.key]);
                    const idx = keys.indexOf(key);
                    if (idx >= 0) {
                        keys.splice(idx, 1);
                        btn.classList.remove('is-selected');
                        btn.setAttribute('aria-pressed', 'false');
                    } else {
                        keys.push(key);
                        btn.classList.add('is-selected');
                        btn.setAttribute('aria-pressed', 'true');
                    }
                    if (keys.length) {
                        this.answers[step.key] = keys.join(',');
                    } else {
                        delete this.answers[step.key];
                    }
                    this.setNextEnabled(
                        this.stage.querySelector('[data-qa-next]'),
                        keys.length > 0
                    );
                    return;
                }

                this.answers[step.key] = key;
                if (step.layout === 'language') {
                    this.language = key;
                    this.applyLabels();
                    // Language change needs fresh step copy for the picked language.
                    await this.refreshResolved({ advance: true, stayOnKey: step.key });
                    return;
                }
                // Single-select: commit and advance locally (no per-step resolve).
                this.advanceLocal({ advance: true, stayOnKey: step.key });
            });
        });

        if (!multi) {
            return;
        }

        const next = this.stage.querySelector('[data-qa-next]');
        if (!next) {
            return;
        }
        this.setNextEnabled(next, parseAnswerKeys(this.answers[step.key]).length > 0);
        next.addEventListener('click', async (e) => {
            e.preventDefault();
            if (this.busy || next.disabled) {
                return;
            }
            const keys = parseAnswerKeys(this.answers[step.key]);
            if (keys.length === 0) {
                return;
            }
            this.hideStatus();
            this.advanceLocal({ advance: true, stayOnKey: step.key });
        });
    }

    bindFields(step) {
        const form = this.stage.querySelector('[data-qa-fields]');
        const next = this.stage.querySelector('[data-qa-next]');
        if (!form || !next) {
            return;
        }

        const syncNext = () => {
            const ok = this.areRequiredFieldsSatisfied(step, form);
            this.setNextEnabled(next, ok);
            if (ok) {
                this.hideStatus();
            }
        };

        form.querySelectorAll('[data-qa-toggle]').forEach((toggle) => {
            toggle.addEventListener('change', () => {
                this.applyToggleVisibility(form, toggle);
                syncNext();
            });
            this.applyToggleVisibility(form, toggle);
        });

        form.querySelectorAll('[data-qa-field]').forEach((input) => {
            input.addEventListener('input', syncNext);
            input.addEventListener('change', syncNext);
        });
        form.querySelectorAll('[data-qa-phone]').forEach((wrap) => {
            mountPhoneField(wrap, {
                flagBaseUrl: this.flagBaseUrl,
                labels: this.labels,
                language: this.language,
                onChange: syncNext,
            });
        });
        syncNext();

        next.addEventListener('click', async (e) => {
            e.preventDefault();
            if (this.busy || next.disabled) {
                return;
            }
            const inputs = [...form.querySelectorAll('[data-qa-field]')];
            for (const input of inputs) {
                if (input.disabled || input.closest('[hidden]')) {
                    continue;
                }
                if (input.getAttribute('data-qa-toggle') === '1') {
                    continue;
                }
                const required = input.getAttribute('data-required') === '1';
                const val = String(input.value || '').trim();
                if (input.getAttribute('data-qa-phone-e164') === '1') {
                    if ((required && val === '') || (val !== '' && !isCoarseE164(val))) {
                        const national = form.querySelector('[data-qa-phone-national]');
                        (national || input).focus();
                        this.setNextEnabled(next, false);
                        this.showStatus(this.labels.phoneInvalid || this.labels.required, true);
                        return;
                    }
                    continue;
                }
                if (required && val === '') {
                    input.focus();
                    this.setNextEnabled(next, false);
                    return;
                }
            }
            inputs.forEach((input) => {
                const key = input.getAttribute('data-qa-field');
                if (!key) {
                    return;
                }
                if (input.getAttribute('data-qa-toggle') === '1') {
                    if (input.checked) {
                        this.answers[key] = '1';
                        const hides = input.getAttribute('data-qa-hides') || '';
                        if (hides) {
                            delete this.answers[hides];
                        }
                    } else {
                        delete this.answers[key];
                    }
                    return;
                }
                if (input.disabled || input.closest('[hidden]')) {
                    delete this.answers[key];
                    return;
                }
                this.answers[key] = String(input.value || '').trim();
            });
            // Mark fields step answered without touching answers[step.key]
            // (step key may equal a field key, e.g. location).
            this.committedFieldsSteps.add(step.key);
            this.advanceLocal({ advance: true, stayOnKey: step.key });
        });
    }

    applyToggleVisibility(form, toggle) {
        const hides = toggle.getAttribute('data-qa-hides') || '';
        if (!hides) {
            return;
        }
        const wrap = form.querySelector(`[data-qa-field-wrap="${hides}"]`);
        const input = form.querySelector(`[data-qa-field="${hides}"]`);
        if (!wrap) {
            return;
        }
        const skip = toggle.checked;
        wrap.hidden = skip;
        if (input) {
            input.disabled = skip;
            if (skip) {
                input.value = '';
            }
        }
        const national = wrap.querySelector('[data-qa-phone-national]');
        if (national) {
            national.disabled = skip;
            if (skip) {
                national.value = '';
            }
        }
    }

    bindSummary() {
        const captchaForm = this.stage.querySelector('[data-qa-captcha]');
        if (captchaForm) {
            captchaForm.addEventListener('submit', (event) => {
                event.preventDefault();
            });
        }
        const submit = this.stage.querySelector('[data-qa-submit]');
        if (submit) {
            submit.addEventListener('click', () => this.submit());
        }
        const roots = this.root.querySelectorAll('.frc-captcha');
        if (roots.length) {
            tryAttachFriendlyCaptcha(roots);
        }
    }

    readCaptchaToken() {
        const field = this.root.querySelector('.frc-captcha [name="frc-captcha-response"]')
            || this.root.querySelector('[name="frc-captcha-response"]');
        return field ? String(field.value || '').trim() : '';
    }

    renderMessage(step) {
        return this.renderAgentRow(this.renderAgentHeading(step));
    }

    bindMessage() {
        this.bindBack();
    }

    bindBack() {
        const back = this.stage.querySelector('[data-qa-back]');
        if (!back) {
            return;
        }
        back.addEventListener('click', () => {
            if (this.busy || this.stepIndex <= 0) {
                return;
            }
            if (this.submitted) {
                const firstPost = this.firstPostSubmitIndex();
                if (firstPost < 0 || this.stepIndex <= firstPost) {
                    return;
                }
                this.stepIndex = this.clampStepIndex(this.stepIndex - 1);
                this.pendingScroll = 'bottom';
                this.render();
                return;
            }
            this.goBack();
        });
    }

    /**
     * Reopen the previous step. Later answers are dropped so a different
     * branch cannot stay unlocked. Field values on the reopened step stay
     * so Back does not empty name / phone / country.
     */
    goBack() {
        if (this.busy || this.stepIndex <= 0) {
            return;
        }
        const targetIndex = this.stepIndex - 1;
        const targetStep = this.visibleSteps()[targetIndex];
        this.clearAnswersFrom(targetIndex + 1);
        if (targetStep?.layout === 'fields') {
            this.committedFieldsSteps.delete(targetStep.key);
            const fieldKeys = new Set((targetStep.fields || []).map((field) => field.key));
            if (!fieldKeys.has(targetStep.key)) {
                delete this.answers[targetStep.key];
            }
        } else {
            this.clearStepAnswers(targetStep);
            if (targetStep?.layout === 'language') {
                this.language = '';
                this.applyLabels();
            }
        }
        this.stepIndex = this.clampStepIndex(targetIndex);
        this.pendingScroll = 'bottom';
        this.render();
    }

    async submit() {
        if (this.busy) {
            return;
        }
        const captchaResponse = this.readCaptchaToken();
        if (this.captchaSiteKey && !this.captchaSkip && !captchaResponse) {
            this.showStatus(this.labels.errorCaptcha || this.labels.errorGeneric || 'Error', true);
            return;
        }
        this.busy = true;
        this.root.classList.add('is-submitting');
        const onLeave = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onLeave);
        const file = this.pendingMedia?.file || null;
        if (file) {
            this.showSubmitOverlay('upload', 0);
        } else {
            this.showSubmitOverlay('create', null);
        }
        try {
            const result = file
                ? await postPluginMultipart(this.submitUrl, {
                    language: this.language,
                    answers: this.answers,
                    captchaResponse,
                    file,
                    onUploadProgress: (pct) => this.showSubmitOverlay('upload', pct),
                    onBodySent: () => this.showSubmitOverlay('process', null),
                })
                : await postPlugin(this.submitUrl, {
                    language: this.language,
                    answers: this.answers,
                    captchaResponse,
                });
            if (!result || !result.success) {
                const fail = new Error(result?.message || 'submit_failed');
                fail.payload = result;
                throw fail;
            }
            this.submitted = true;
            this.uploadToken = String(result.resumeToken || result.uploadToken || '');
            if (this.uploadToken) {
                writeResumeTokenToUrl(this.uploadToken);
            }
            this.hideSubmitOverlay();
            const firstPost = this.firstPostSubmitIndex();
            if (firstPost >= 0) {
                this.stepIndex = firstPost;
                this.hideStatus();
                this.pendingScroll = 'bottom';
                this.render();
            } else {
                this.done = true;
                this.stage.innerHTML = `
					<div class="quick-apply__done">
						<p class="quick-apply__success">${escapeHtml(result.message || this.labels.successStub)}</p>
					</div>
				`;
                this.hideStatus();
                this.pendingScroll = 'bottom';
                this.runPendingScroll();
            }
        } catch (e) {
            this.hideSubmitOverlay();
            const fromServer = e?.payload?.message;
            this.showStatus(fromServer || this.labels.errorGeneric || 'Error', true);
        } finally {
            window.removeEventListener('beforeunload', onLeave);
            this.busy = false;
            this.root.classList.remove('is-submitting');
        }
    }

    showSubmitOverlay(phase, percent) {
        const overlay = this.submitOverlay || this.root.querySelector('[data-qa-submit-overlay]');
        const labelEl = overlay?.querySelector('[data-qa-submit-phase]');
        const hintEl = overlay?.querySelector('[data-qa-submit-hint]');
        const bar = overlay?.querySelector('[data-qa-submit-bar]');
        if (!overlay) {
            return;
        }
        this.submitOverlay = overlay;
        overlay.hidden = false;
        if (overlay.parentElement !== document.body) {
            this.submitOverlayHome = overlay.parentElement;
            document.body.appendChild(overlay);
        }
        let text = this.labels.loading || '…';
        if (phase === 'upload') {
            text = this.labels.submitPhaseUpload || 'Uploading video…';
            if (percent != null && percent > 0) {
                text = `${text} ${percent}%`;
            }
        } else if (phase === 'process') {
            text = this.labels.submitPhaseProcess || 'Processing video…';
        } else if (phase === 'create') {
            text = this.labels.submitPhaseCreate || 'Creating application…';
        }
        if (labelEl) {
            labelEl.textContent = text;
        }
        if (hintEl) {
            hintEl.textContent = this.labels.submitPleaseWait || 'Please wait. This may take a moment.';
        }
        overlay.classList.toggle('is-indeterminate', phase !== 'upload' || percent == null || percent <= 0);
        if (bar) {
            if (phase === 'upload' && percent != null && percent > 0) {
                bar.style.width = `${Math.max(0, Math.min(100, percent))}%`;
            } else {
                bar.style.width = '30%';
            }
        }
    }

    hideSubmitOverlay() {
        const overlay = this.submitOverlay;
        if (!overlay) {
            return;
        }
        overlay.hidden = true;
        overlay.classList.remove('is-indeterminate');
        if (this.submitOverlayHome && overlay.parentElement === document.body) {
            this.submitOverlayHome.appendChild(overlay);
        }
        this.submitOverlayHome = null;
    }
}

function boot() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
        if (root.__quickApplyBooted) {
            return;
        }
        root.__quickApplyBooted = true;
        // eslint-disable-next-line no-new
        new QuickApplyApp(root);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
